import { useEffect, useRef } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { useIsRestoring, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { useOnlineStatus } from "@/offline/online-status";
import { clearAllOfflineStores } from "@/offline/db";
import {
  getCachedIdentity,
  setCachedIdentity,
  workOSUserToCachedIdentity,
} from "@/offline/identity";
import {
  offlineIdentityKey,
  useOfflineIdentity,
} from "@/hooks/use-offline-identity";
import { useResyncPinnedProjects } from "@/offline/use-resync";
import { useOutboxDrain } from "@/hooks/use-outbox-drain";
import { useAppendDrain } from "@/hooks/use-append-drain";
import { userKeys } from "@/lib/query-keys";
import { useSentryUser } from "@/hooks/use-sentry-user";
import { OtusLoader } from "@/components/common/otus-loader/otus-loader";

import type { User } from "@workos-inc/authkit-react";
import type { CachedIdentity } from "@/offline/db";

function identityRowMatches(cached: CachedIdentity, user: User): boolean {
  const next = workOSUserToCachedIdentity(user);
  return (
    cached.id === next.id &&
    cached.email === next.email &&
    cached.firstName === next.firstName &&
    cached.lastName === next.lastName &&
    cached.avatarUrl === next.avatarUrl
  );
}

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const { user, isLoading, signIn } = useAuth();
  const isRestoring = useIsRestoring();
  const isOnline = useOnlineStatus();
  const identity = useOfflineIdentity();
  const queryClient = useQueryClient();

  // Attach the signed-in identity (live WorkOS user, else the cached offline
  // identity) to Sentry's global scope so every event names who hit it, and
  // clear it when signed out. See [[error-reporting]] and use-sentry-user.
  useSentryUser(user, identity.data);

  // Top up pinned projects on first authenticated mount and on each
  // offline→online flip. No-op while offline or signed out; failures land in
  // Sentry. Mounted here so it runs once at the auth root instead of inside
  // each route.
  useResyncPinnedProjects();

  // Background sync engine for the offline record outbox: drains queued
  // records, retries transient failures forever with backoff, pauses while
  // offline. Mounted once at the auth root so a single drain owns the
  // outbox regardless of which route is visible. No-op when the outbox is
  // empty; its uploader mutation pauses itself while offline.
  useOutboxDrain();

  // Sibling background sync for the APPEND outbox: drains photos queued onto
  // existing, synced records, with the same retry/idempotency discipline.
  // Mounted next to the record-create drain so one append drain owns the
  // append outbox regardless of route. No-op when empty; pauses while offline.
  useAppendDrain();

  // Track whether WorkOS ever resolved this session. If we boot offline,
  // `useAuth()` stays in `isLoading: true` forever because AuthKitProvider's
  // `createClient(...).then(...)` chain never settles (no `.catch()`).
  // On online-flip we need a hard reload to retrigger that effect.
  const everResolvedRef = useRef(false);
  useEffect(() => {
    if (!isLoading) everResolvedRef.current = true;
  }, [isLoading]);

  // Online-flip handler: false → true transition.
  const prevOnlineRef = useRef(isOnline);
  useEffect(() => {
    const wasOffline = !prevOnlineRef.current;
    prevOnlineRef.current = isOnline;
    if (!isOnline || !wasOffline) return;

    if (!everResolvedRef.current) {
      // Booted offline-bypass via cached identity. AuthKitProvider's
      // createClient never resolved and there's no API to retrigger it
      // without a remount. Reload — the next mount runs with network.
      window.location.reload();
      return;
    }

    // We had a live session before; just invalidate the user query so
    // useCurrentUser refetches fresh data now that the network is back.
    queryClient.invalidateQueries({ queryKey: userKeys.me() });
  }, [isOnline, queryClient]);

  // When WorkOS resolves online with a user, sync the cached identity. On id
  // mismatch (multi-user device), wipe stores + QueryClient before adopting
  // the new user so the previous user's persisted data doesn't leak.
  //
  // Dependencies intentionally exclude `identity.data` — we read the cached
  // row imperatively inside the effect via `getCachedIdentity()`. Including
  // it caused a write loop: `setCachedIdentity` → `invalidateQueries` →
  // refetch → `identity.data` changed → effect re-fires → write again.
  // The effect only needs to react to user / loading / online transitions.
  useEffect(() => {
    if (isLoading || !user || !isOnline) return;

    let cancelled = false;
    (async () => {
      const cached = await getCachedIdentity();
      if (cancelled) return;

      if (cached && cached.id !== user.id) {
        await clearAllOfflineStores();
        if (cancelled) return;
        queryClient.clear();
        if (cancelled) return;
      } else if (cached && identityRowMatches(cached, user)) {
        // Already in sync with the live user — no write needed. Skipping
        // here is what closes the infinite-loop trap (see header comment).
        return;
      }

      await setCachedIdentity(user);
      if (cancelled) return;
      queryClient.invalidateQueries({ queryKey: offlineIdentityKey });
    })().catch((err) => {
      Sentry.captureException(err, {
        tags: { area: "offline-init", phase: "sync-identity" },
      });
    });

    return () => {
      cancelled = true;
    };
  }, [user, isLoading, isOnline, queryClient]);

  // Standard signIn redirect when online and WorkOS reports no user.
  // Guarded on `isOnline` so a transient offline → "no user" race (e.g.
  // sign-out failing on a flaky network) can't dispatch a sign-in redirect
  // that would itself fail offline.
  useEffect(() => {
    if (!isOnline) return;
    if (!isLoading && !user) {
      signIn();
    }
  }, [isOnline, isLoading, user, signIn]);

  // Wait for the persister and offline-identity probe before deciding.
  if (isRestoring || identity.isLoading) return <OtusLoader fullScreen />;

  // Block render while a user-id mismatch is being resolved by the sync
  // effect. Without this, the first paint of a fresh login on a shared
  // device shows the previous user's persisted projects / records / plans
  // before the effect's clear runs — exactly the leak this PR exists to
  // close. We stay on the loader until the effect wipes stores, writes the
  // new identity, and the offline-identity refetch settles.
  const cachedId = identity.data?.id;
  const isMismatch =
    isOnline &&
    !isLoading &&
    user != null &&
    cachedId !== undefined &&
    cachedId !== user.id;
  if (isMismatch) return <OtusLoader fullScreen />;

  if (!isOnline) {
    if (identity.data) return <>{children}</>;
    return <OfflineSignInGate />;
  }

  if (isLoading) return <OtusLoader fullScreen />;
  if (!user) return null;

  return <>{children}</>;
}

function OfflineSignInGate() {
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-black px-6 text-center text-white">
      <h1 className="text-xl font-semibold">You're offline</h1>
      <p className="max-w-sm text-sm text-white/70">
        Connect to the internet once to sign in to Otus. After that, you'll be
        able to open the app and view your pinned projects without a
        connection.
      </p>
    </div>
  );
}
